import React from 'react';

import type { AsyncStatus } from '../../../../../@types/AsyncStatus';
import classNames from '../../../../../utils/classNames';
import { coproTypes } from '../../../../../utils/copros';
import Badge from '../../../../common/Badge';
import Table from '../../../../common/Table';
import type { RnicEntry, SelectedCopro } from '../../types';

interface CoproTableData {
  [key: string]: unknown;
  id: number;
  '#': React.ReactNode;
  'N° IMMAT / TYPE': React.ReactNode;
  NOM: string;
  ADRESSE: string;
  'NB. ADR. / PAR.': React.ReactNode;
}

interface CoproListProps {
  copros: SelectedCopro[];
  rnics: RnicEntry[];
  selectedCoproId: number | null;
  onSelectCopro: (copro: SelectedCopro) => void;
  status: AsyncStatus;
}

const sortableColumns = {
  'N° IMMAT / TYPE': 'string',
  NOM: 'string',
  ADRESSE: 'string',
};

const CoproList: React.FC<CoproListProps> = ({
  copros,
  rnics,
  selectedCoproId,
  onSelectCopro,
  status,
}) => {
  const formatCopro = (item: SelectedCopro): CoproTableData => {
    const hasValidAddress = item.addresses.length > 0;
    const hasPlots = item.plots.length > 0;
    const isFullyLocalized = hasValidAddress && hasPlots;
    // Edge case: BAN returned no address, so the copro is localized via parcelles only.
    const isPlotsOnly = !hasValidAddress && hasPlots;
    const isLocalized = isFullyLocalized || isPlotsOnly;
    const isSelected = selectedCoproId === item.id;

    const rnicEntry = rnics.find((coproRNIC) => coproRNIC.numeroDimmatriculation === item.remoteId);
    const addressDisplay = hasValidAddress
      ? `${item.addresses[0].address} - ${item.addresses[0].postalCode} - ${item.addresses[0].city}`
      : rnicEntry?.adresseDeReference || '';

    const typeLabel = coproTypes[item.type] || '';
    const immatOrType = item.remoteId
      ? item.remoteId
      : (
        <span className="rounded bg-rgpd-blue-light px-2 py-1 text-xs font-medium tracking-wide whitespace-nowrap text-white uppercase">
          {typeLabel}
        </span>
      );

    let buttonColorClass = 'bg-rgpd-blue-dark';
    if (isSelected) {
      buttonColorClass = 'bg-rgpd-blue-light ring-2 ring-rgpd-blue-dark';
    } else if (isFullyLocalized) {
      buttonColorClass = 'bg-rgpd-green';
    } else if (isPlotsOnly) {
      buttonColorClass = 'bg-orange-500 hover:bg-orange-600';
    }

    const buttonTitle = isPlotsOnly
      ? 'Localisation validée via parcelles uniquement (aucune adresse disponible dans la BAN)'
      : undefined;

    return {
      id: item.id,
      '#': (
        <div className="align-center flex justify-center">
          <button
            className={classNames(
              'w-full md:w-auto text-white text-md rounded shadow-md align-center whitespace-nowrap py-3 px-5 my-1',
              buttonColorClass,
            )}
            onClick={() => onSelectCopro(item)}
            title={buttonTitle}
          >
            {isLocalized ? 'Consulter la localisation' : 'Valider la localisation'}
          </button>
        </div>
      ),
      'N° IMMAT / TYPE': immatOrType,
      NOM: item.name,
      ADRESSE: addressDisplay,
      'NB. ADR. / PAR.': <div className="align-center flex justify-center whitespace-nowrap">{item.addresses.length} / {item.plots.length}</div>,
    };
  };

  const formattedData = copros.map((copro) => formatCopro(copro));

  if (status === 'ERROR') {
    return (
      <div className="flex w-full place-content-center">
        <Badge
          mode="error"
          text="Erreur lors du chargement des données"
        />
      </div>
    );
  }

  return (
    <>
      <h2 className="mt-8 mb-8 text-center text-xl font-semibold text-rgpd-blue-light">LISTE DES COPROPRIÉTÉS</h2>
      <Table
        isSearchable
        sortableColumns={sortableColumns}
        data={formattedData}
        headers={['N° IMMAT / TYPE', 'NOM', 'ADRESSE', 'NB. ADR. / PAR.', '#']}
        tableClass="text-sm"
        stickyHeader
      />
      {formattedData.length === 0 && (
        <div className="flex w-full place-content-center">
          <Badge
            mode="warning"
            text="Aucune copropriété trouvée"
            className="mt-6 mb-4"
          />
        </div>
      )}
    </>
  );
};

export default CoproList;
